
import { supabase } from "@/integrations/supabase/client";

export const performEnhancedFallbackSearch = async (query: string) => {
  try {
    console.log('Performing enhanced fallback search for:', query);
    
    const searchTerm = query.toLowerCase().trim();
    const searchTerms = searchTerm.split(' ').filter(term => term.length > 2);
    
    // Check if this is a specific search that we know we don't have
    const unavailableDestinations = [
      'afrika', 'safari door afrika', 'kenia', 'tanzania', 'botswana', 'zambia',
      'australië', 'nieuw-zeeland', 'japan', 'china', 'brazilië', 'argentinië'
    ];
    
    const isUnavailableDestination = unavailableDestinations.some(dest => 
      searchTerm.includes(dest)
    );
    
    if (isUnavailableDestination) {
      console.log('Query matches unavailable destination, returning empty results');
      return [];
    }

    // Check for location + criteria pattern
    const locationTerms = ['italië', 'italie', 'spanje', 'griekenland', 'turkije', 'frankrijk', 'portugal'];
    const detectedLocation = searchTerms.find(term => 
      locationTerms.some(loc => term.includes(loc) || loc.includes(term))
    );
    
    if (detectedLocation) {
      console.log('Fallback: Detected location-based search:', detectedLocation);
      
      // Map location variations
      let countryFilter = detectedLocation;
      if (detectedLocation.includes('itali')) countryFilter = 'Italië';
      else if (detectedLocation.includes('span')) countryFilter = 'Spanje';
      else if (detectedLocation.includes('grieken')) countryFilter = 'Griekenland';
      else if (detectedLocation.includes('turk')) countryFilter = 'Turkije';
      else if (detectedLocation.includes('frank')) countryFilter = 'Frankrijk';
      else if (detectedLocation.includes('portugal')) countryFilter = 'Portugal';
      
      // First try: exact country match with broad criteria
      const { data, error } = await supabase
        .from('Complete feed')
        .select('*')
        .not('imageURL', 'is', null)
        .not('name', 'is', null)
        .eq('country', countryFilter)
        .limit(100);
      
      if (error) throw error;
      
      console.log(`Enhanced fallback found ${data?.length || 0} results for location "${countryFilter}"`);
      
      const validatedData = (data || []).map((vacation: any) => ({
        ...vacation,
        name: vacation.name || "Onbekende vakantie",
        imageURL: vacation.imageURL || "/placeholder.svg"
      }));
      
      return validatedData;
    }
    
    // Original fallback logic for non-location searches
    let conditions: string[] = [];
    
    // Zoek op volledige query
    conditions.push(
      `region.ilike.%${searchTerm}%`,
      `city.ilike.%${searchTerm}%`,
      `country.ilike.%${searchTerm}%`,
      `name.ilike.%${searchTerm}%`,
      `description.ilike.%${searchTerm}%`,
      `facilities.ilike.%${searchTerm}%`,
      `specialCategories.ilike.%${searchTerm}%`,
      `typeAccommodatie.ilike.%${searchTerm}%`,
      `typeVakantie.ilike.%${searchTerm}%`
    );
    
    // Voor multi-term queries, zoek ook op individuele termen
    if (searchTerms.length > 1) {
      searchTerms.forEach(term => {
        conditions.push(
          `region.ilike.%${term}%`,
          `city.ilike.%${term}%`,
          `country.ilike.%${term}%`,
          `name.ilike.%${term}%`,
          `description.ilike.%${term}%`,
          `facilities.ilike.%${term}%`,
          `specialCategories.ilike.%${term}%`,
          `typeAccommodatie.ilike.%${term}%`,
          `typeVakantie.ilike.%${term}%`
        );
      });
    }

    const { data, error } = await supabase
      .from('Complete feed')
      .select('*')
      .not('imageURL', 'is', null)
      .not('name', 'is', null)
      .or(conditions.join(','))
      .limit(100);
    
    if (error) throw error;
    
    console.log(`Enhanced fallback search found ${data?.length || 0} results for "${query}"`);
    
    const validatedData = (data || []).map((vacation: any) => ({
      ...vacation,
      name: vacation.name || "Onbekende vakantie",
      imageURL: vacation.imageURL || "/placeholder.svg"
    }));
    
    return validatedData;
  } catch (error: any) {
    console.error('Enhanced fallback search error:', error);
    throw error;
  }
};

export const performBroadSearch = async (query: string) => {
  try {
    console.log('Performing broad search as last resort for:', query);
    
    // Only do broad search for very generic terms
    const searchTerm = query.toLowerCase().trim();
    const isGenericSearch = 
      searchTerm === 'vakantie' || 
      searchTerm === 'reis' || 
      searchTerm === 'weekend' ||
      searchTerm.length <= 3;
    
    if (!isGenericSearch) {
      console.log('Not a generic search, avoiding broad results');
      return [];
    }
    
    // Very broad search - just look for any vacation with good images
    const { data, error } = await supabase
      .from('Complete feed')
      .select('*')
      .not('imageURL', 'is', null)
      .not('name', 'is', null)
      .limit(50);
    
    if (error) throw error;
    
    console.log(`Broad search found ${data?.length || 0} results`);
    
    const validatedData = (data || []).map((vacation: any) => ({
      ...vacation,
      name: vacation.name || "Onbekende vakantie",
      imageURL: vacation.imageURL || "/placeholder.svg"
    }));
    
    return validatedData;
  } catch (error: any) {
    console.error('Broad search error:', error);
    throw error;
  }
};
